import type { CatalogMediaUploadInitiation } from '@emazy/shared-types';

export type UploadProgress = {
  loadedBytes: number;
  totalBytes: number;
  percentage: number;
};

export interface CatalogMediaUploadTransport {
  upload(input: {
    initiation: CatalogMediaUploadInitiation;
    file: File;
    signal?: AbortSignal;
    onProgress?: (progress: UploadProgress) => void;
  }): Promise<void>;
}

export class BrowserCatalogMediaUploadTransport implements CatalogMediaUploadTransport {
  upload(input: Parameters<CatalogMediaUploadTransport['upload']>[0]) {
    return new Promise<void>((resolve, reject) => {
      const request = new XMLHttpRequest();
      const aborted = () => request.abort();
      if (input.signal?.aborted) return reject(new DOMException('Upload canceled.', 'AbortError'));
      input.signal?.addEventListener('abort', aborted, { once: true });
      request.open(input.initiation.method, input.initiation.uploadUrl);
      request.upload.addEventListener('progress', (event) => {
        const total = event.lengthComputable ? event.total : input.file.size;
        input.onProgress?.({
          loadedBytes: Math.min(event.loaded, total),
          totalBytes: total,
          percentage: total ? Math.min(100, Math.round((event.loaded / total) * 100)) : 0,
        });
      });
      request.addEventListener('load', () => {
        input.signal?.removeEventListener('abort', aborted);
        if (request.status >= 200 && request.status < 300) resolve();
        else reject(new Error('The direct image upload was rejected. Start a new upload.'));
      });
      request.addEventListener('error', () =>
        reject(new Error('The image upload was interrupted.')),
      );
      request.addEventListener('abort', () =>
        reject(new DOMException('Upload canceled.', 'AbortError')),
      );
      const form = new FormData();
      for (const [key, value] of Object.entries(input.initiation.fields)) form.append(key, value);
      form.append('file', input.file, input.file.name);
      request.send(form);
    });
  }
}
